import { type Evaluated } from '@/domain/construction';
import { lines } from '@/domain/geometry';
import { type Vec2, vec } from '@/domain/math';
import {
  createEntity,
  type Entity,
  type EntityId,
  type SceneDocument,
  type Style,
} from '@/domain/model';

import { type EditAction } from './actions';

/**
 * Lifting one edge of a solid out as an object of its own.
 *
 * ## The report
 *
 * "nhiều khi muốn 1 đoạn nào trong khối có style khác (màu sắc hay kiểu nét)" — highlight `SA`
 * in red, or draw the altitude dashed. A solid is a single entity with a single style, so there
 * was no way to say it.
 *
 * ## Why this rather than selecting a sub-edge
 *
 * Because everything addressable in this editor is an entity, and a selection is a set of entity
 * ids. "The third edge of solid S" would be a second, parallel notion of what is selected, and
 * six places would have to learn it: the selection state, hit-testing, the properties panel, the
 * object list, the file format, and all three exporters — to end up doing what an entity already
 * does.
 *
 * So the edge *becomes* an entity. From that moment every control already built applies to it
 * with no new code: colour, dash, width, opacity, hide, lock, a name, copy and paste, the
 * hidden-line dashing, and the SVG and PDF writers. And it gains something a sub-selection could
 * not have given it — it is a named object, so the figure can measure it and a problem can talk
 * about it.
 *
 * ## What the solid then does
 *
 * Stops drawing that edge, so a dashed red `SA` is not laid over a solid black one showing
 * through its gaps. That is decided at drawing time rather than written into the solid — see
 * `coveredSegments` — so deleting the segment brings the edge back and the document is never
 * left holding a note about a line that no longer exists.
 */

/**
 * The side of a polygon nearest a point, as the two corners it joins.
 *
 * "tôi vẽ đáy bằng công cụ đa giác, nhưng giờ ấn alt + click vào cạnh nó không cho chọn cạnh" —
 * the same want as for a solid's edge, on the shape a teacher reaches for just as often. A base
 * drawn with the polygon tool has sides, and one of them is the one the question is about.
 *
 * Simpler than the solid case, because a polygon's corners are already objects: the side is
 * lifted as a segment between the two of them, with no new points to make.
 */
export function polygonSideAt(
  entity: Entity,
  value: Evaluated,
  world: Vec2,
  tolerance: number
): readonly [EntityId, EntityId] | null {
  if (entity.source.op !== 'polygon' || value.kind !== 'polygon') return null;

  const corners = entity.source.vertices;
  const drawn = value.vertices;
  if (corners.length < 3 || corners.length !== drawn.length) return null;

  let best: { side: readonly [EntityId, EntityId]; distance: number } | null = null;
  for (const [index, from] of drawn.entries()) {
    const next = (index + 1) % drawn.length;
    const to = drawn[next];
    const a = corners[index];
    const b = corners[next];
    if (to === undefined || a === undefined || b === undefined) continue;

    const distance = vec.distance(world, lines.closestPointOnSegment({ a: from, b: to }, world));
    if (distance > tolerance) continue;
    if (best === null || distance < best.distance) best = { side: [a, b], distance };
  }

  return best?.side ?? null;
}

/**
 * The edge of a solid nearest a point, as the pair of corner indices it joins.
 *
 * `null` for a solid with no countable edges — a sphere, a cylinder — where the drawn straight
 * parts are silhouettes rather than edges of the object, and for a click that landed too far
 * from any of them.
 */
export function solidEdgeAt(
  value: Evaluated,
  world: Vec2,
  tolerance: number
): readonly [number, number] | null {
  if (value.kind !== 'solid') return null;
  const points = value.points;
  const edges = value.edges;
  if (points === undefined || edges === undefined) return null;

  let best: { edge: readonly [number, number]; distance: number } | null = null;
  for (const [a, b] of edges) {
    const from = points[a];
    const to = points[b];
    if (from === undefined || to === undefined) continue;

    const distance = vec.distance(world, lines.closestPointOnSegment({ a: from, b: to }, world));
    if (distance > tolerance) continue;
    if (best === null || distance < best.distance) best = { edge: [a, b], distance };
  }

  return best?.edge ?? null;
}

/**
 * The edits that lift `edge` out of `solid` as a segment carrying `style`.
 *
 * Corners already lifted are reused rather than stacked on, so promoting `SA` and then `SB`
 * gives three points and two segments and not four points. `existing` names a segment that
 * already joins these two corners, which the caller selects instead of making a second one —
 * a user who alt-clicks the same edge twice means "that one", not "another one".
 */
export function promoteActions(
  document: SceneDocument,
  solid: EntityId,
  edge: readonly [number, number],
  style: Style,
  mint: () => EntityId
): {
  readonly actions: readonly EditAction[];
  readonly created: readonly EntityId[];
  readonly existing: EntityId | null;
} {
  const actions: EditAction[] = [];
  const created: EntityId[] = [];

  const cornerOf = (index: number): EntityId => {
    const found = [...document.entities.values()].find(
      (entity) =>
        entity.source.op === 'solid-vertex' &&
        entity.source.solid === solid &&
        entity.source.index === index
    );
    if (found !== undefined) return found.id;

    const id = mint();
    /*
     * Invisible, and not scaffolding to be swept away either. The user asked for a coloured
     * edge, not for two new dots on their figure — but the corner is a real construction that
     * the segment depends on, and showing it would be answering a question nobody asked.
     */
    actions.push({
      kind: 'add-entity',
      entity: createEntity(id, { op: 'solid-vertex', solid, index }, style, { visible: false }),
    });
    created.push(id);
    return id;
  };

  const [from, to] = edge;
  const a = cornerOf(from);
  const b = cornerOf(to);

  const already = joining(document, a, b);
  if (already !== null) return { actions: [], created: [], existing: already };

  const id = mint();
  actions.push({ kind: 'add-entity', entity: createEntity(id, { op: 'segment', a, b }, style) });
  created.push(id);

  return { actions, created, existing: null };
}

/** A segment already joining these two corners, whichever way round it was written. */
function joining(document: SceneDocument, a: EntityId, b: EntityId): EntityId | null {
  const match = (entity: Entity): boolean =>
    entity.source.op === 'segment' &&
    ((entity.source.a === a && entity.source.b === b) ||
      (entity.source.a === b && entity.source.b === a));

  return [...document.entities.values()].find(match)?.id ?? null;
}
